import React, {
  FormEvent,
  FunctionComponent,
  useEffect,
  useState
} from "react";
import { AppConfig, ClassName } from "../utils/helpers/types";
import Modal, { ModalProps } from "../components/modal/Modal";
import {
  fetchClassNamesForFileUpload,
  fetchInheritedModules,
  uploadFilesApi
} from "../utils/helpers/data/task-creation";
import styles from "./self-service.module.scss";
import { MultiFileUploader } from "../components/multi-file-uploader/MultiFileUploader";
import Select from "../components/select/Select";
import Input from "../components/input/Input";
import { resources } from "../utils/resources";
import Button from "../components/button/Button";

const blankAppConfig: AppConfig = { name: "", value: "" };
interface FileUploadModalProps extends ModalProps {
  taskClass: string;
  setTaskClass: (name: string) => void;
  inheritedModule: string;
  setInheritedModule: (name: string) => void;
  filesToUpload: File[];
  setFilesToUpload: (files: File[]) => void;
  notify: (type: "success" | "error", message: string) => void;
  onUploadSuccess: () => void;
}

const FileUploader: FunctionComponent<FileUploadModalProps> = ({
  visible,
  cancel,
  taskClass,
  setTaskClass,
  inheritedModule,
  setInheritedModule,
  filesToUpload,
  setFilesToUpload,
  notify,
  onUploadSuccess
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [appConfigs, setAppConfigs] = useState<AppConfig[]>([
    { ...blankAppConfig }
  ]);
  const [classNames, setClassNames] = useState<ClassName[]>([]);
  const [inheritedModules, setInheritedModules] = useState<string[]>([]);
  const [isFetchingInheritedModules, setIsFetchingInheritedModules] =
    useState(false);

  useEffect(() => {
    if (!visible) {
      setTaskClass("");
      setInheritedModule("");
      setAppConfigs([{ ...blankAppConfig }]);
      setFilesToUpload([]);
      setInheritedModules([]);
    }
  }, [visible, setTaskClass, setInheritedModule, setFilesToUpload]);

  useEffect(() => {
    const fetchData = async () => {
      const options = await fetchClassNamesForFileUpload();
      if (!options.error) {
        const classNamesData: ClassName[] = options.data || [];
        setClassNames(classNamesData);
      }
    };
    fetchData();
  }, []);

  const handleTaskClassSelect = async (value: string) => {
    setTaskClass(value);
    setInheritedModule("");
    setInheritedModules([]);

    setIsFetchingInheritedModules(true);

    try {
      const selectedClass = classNames.find(
        className => className.className === value
      );
      if (selectedClass) {
        const response = await fetchInheritedModules(selectedClass.fullClass);
        if (!response.error) {
          setInheritedModules(response.data || []);
        }
      }
    } catch (error) {
      console.error("Unable to fetch inherited modules: ", error);
    } finally {
      setIsFetchingInheritedModules(false);
    }
  };

  const handleAddConfig = () => {
    setAppConfigs(prev => [...prev, { ...blankAppConfig }]);
  };

  const handleRemoveConfig = (index: number) => {
    if (appConfigs.length > 1) {
      setAppConfigs(prev => prev.filter((_, i) => i !== index));
    } else {
      setAppConfigs([{ ...blankAppConfig }]);
    }
  };

  const handleConfigChange = (
    index: number,
    field: "name" | "value",
    newValue: string
  ) => {
    setAppConfigs(prev =>
      prev.map((config, i) =>
        i === index ? { ...config, [field]: newValue } : config
      )
    );
  };

  const handleUploadSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!taskClass.trim()) {
      notify("error", "Task Class is required.");
      return;
    }

    if (filesToUpload.length === 0) {
      notify("error", "Please select at least one file to upload.");
      return;
    }

    if (filesToUpload.some(file => !file.name.toLowerCase().endsWith(".ipm"))) {
      notify(
        "error",
        "Only files with the '.ipm' extension are accepted for upload."
      );
      return;
    }

    const validAppConfigs = appConfigs.filter(
      config => config.name.trim() && config.value.trim()
    );

    const hasIncompleteConfig = appConfigs.some(
      config =>
        (config.name.trim() && !config.value.trim()) ||
        (!config.name.trim() && config.value.trim())
    );

    if (hasIncompleteConfig) {
      notify(
        "error",
        "Configuration pairs must have both a Name and a Value, or neither."
      );
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();

      if (inheritedModule.trim()) {
        formData.append("inheritedModule", inheritedModule);
      }

      validAppConfigs.forEach((config, index) => {
        formData.append("config[]", JSON.stringify(config));
      });

      filesToUpload.forEach(file => {
        formData.append("files", file);
      });

      const { error, message } = await uploadFilesApi(formData, taskClass);

      if (error) {
        notify("error", `File upload failed: ${message}`);
        return;
      }

      notify(
        "success",
        `Successfully uploaded ${filesToUpload.length} file(s) for task class: ${taskClass}`
      );
      cancel?.();
      onUploadSuccess();
    } catch (error) {
      console.error("Upload API call error:", error);
      notify(
        "error",
        `File upload failed: ${error.message || "An unknown error occurred"}`
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleUploadSubmit}>
        <h2 className="title vertical-margin thin">Upload Files</h2>

        <div style={{ marginTop: "15px", marginBottom: "10px" }}>
          <MultiFileUploader
            acceptedTypes={[".ipm"]}
            titleText="Click or Drag .ipm Files Here"
            subtitle="Only accepts .ipm files"
            onFilesChange={setFilesToUpload}
            currentFiles={filesToUpload}
          />
        </div>

        <div className="input-group">
          <span className="question">Task Class (Module Name)</span>

          <Select
            value={taskClass || ""}
            onSelect={(value: any) => handleTaskClassSelect(value)}
            placeholder="Select task class"
            options={classNames.map(className => ({
              label: className.className,
              value: className.className
            }))}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Inherited Module (Optional)</span>
          {isFetchingInheritedModules && (
            <div
              style={{ color: "green", fontSize: "12px", marginBottom: "5px" }}
            >
              Fetching inherited modules...
            </div>
          )}
          <Select
            value={inheritedModule || ""}
            onSelect={(value: any) => setInheritedModule(value)}
            placeholder="Select inherited module"
            options={inheritedModules.map(inheritedModule => ({
              label: inheritedModule,
              value: inheritedModule
            }))}
            disabled={!taskClass || inheritedModules.length === 0}
            responsive
          />
        </div>

        <span
          className="question"
          style={{ display: "block", marginTop: "15px", fontWeight: "bold" }}
        >
          Task Configurations (Optional)
        </span>

        {appConfigs.map((config, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px"
            }}
          >
            <div
              className="input-group"
              style={{ flex: 1, marginRight: "5px" }}
            >
              <Input
                value={config.name}
                onChange={value => handleConfigChange(index, "name", value)}
                placeholder="Name"
                responsive
              />
            </div>
            <span style={{ marginRight: "5px" }}>:</span>
            <div className="input-group" style={{ flex: 1 }}>
              <Input
                value={config.value}
                onChange={value => handleConfigChange(index, "value", value)}
                placeholder="Value"
                responsive
              />
            </div>
            <span style={{ marginRight: "5px" }} />
            {appConfigs.length > 1 && (
              <img
                onClick={() => handleRemoveConfig(index)}
                src={resources.icons.COLOR_CANCEL}
                alt="clear"
                className={styles.clear}
              />
            )}
            <span style={{ marginRight: "5px" }} />
            {index === appConfigs.length - 1 && (
              <Button
                type="primary"
                size="small"
                onClick={handleAddConfig}
                style={{
                  marginLeft: appConfigs.length > 1 ? "0" : "5px",
                  color: "white",
                  padding: "0 5px"
                }}
              >
                +
              </Button>
            )}
          </div>
        ))}

        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>

          <Button
            buttonType="submit"
            loading={isUploading}
            disabled={
              filesToUpload.length === 0 ||
              !taskClass.trim() ||
              filesToUpload.some(
                file => !file.name.toLowerCase().endsWith(".ipm")
              )
            }
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FileUploader;
